// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Card, Radio } from '@mui/material';
import { Label } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import type { OnboardingQuestion } from '@shared/advisory/account-workflow/types';

export interface QuestionCardProps<TFormData extends FieldValues> {
  question: OnboardingQuestion;
  control: Control<TFormData>;
  name: Path<TFormData>;
  variant?: 'card' | 'plain';
  containerClassName?: string;
}

export const QuestionCard = <TFormData extends FieldValues>({
  question,
  control,
  name,
  variant = 'card',
  containerClassName,
}: QuestionCardProps<TFormData>) => {
  const fieldsetContent = (
    <fieldset className="space-y-6">
      <Label
        component="legend"
        className="text-lg font-medium !leading-6 !text-secondary-dark"
        required
        aria-required="true"
      >
        <Translation i18nKey={question.label[0] as any} ns={question.label[1].ns} />
      </Label>

      <div className="flex flex-col gap-3">
        {question.options.map(option => (
          <Controller
            key={option.id}
            control={control}
            name={name}
            render={({ field }) => (
              <div
                className="flex items-center space-x-2"
                data-testid={`option-${option.id}`}
              >
                <Radio
                  id={option.id}
                  data-testid={`radio-${option.id}`}
                  name={question.id}
                  checked={field.value === option.value}
                  onChange={e => field.onChange(e.target.value)}
                  value={option.value}
                  className="!p-0"
                  required
                  disabled={option.disabled}
                />
                <Label
                  htmlFor={option.id}
                  className={`flex flex-1 ${
                    option.disabled
                      ? 'cursor-not-allowed opacity-60'
                      : 'cursor-pointer'
                  } !text-base !font-normal !leading-6 !text-secondary-dark`}
                  data-testid={`label-${option.id}`}
                >
                  <Translation
                    i18nKey={option.label[0] as any}
                    ns={option.label[1].ns}
                  />
                </Label>
              </div>
            )}
          />
        ))}
      </div>
    </fieldset>
  );

  if (variant === 'plain') {
    return (
      <div data-testid={`question-${question.id}`} className={containerClassName}>
        {fieldsetContent}
      </div>
    );
  }

  return (
    <Card
      variant="outlined"
      data-testid={`question-${question.id}`}
      className={containerClassName || '!rounded-lg !p-7'}
    >
      {fieldsetContent}
    </Card>
  );
};
